import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    User,
    Phone,
    Mail,
    Calendar,
    FileCheck,
    AlertTriangle,
    Loader2,
    RefreshCw,
    Receipt,
    CreditCard,
    ImageIcon,
    ExternalLink,
    Search,
    X,
} from 'lucide-react';

const STATUS_CONFIG = {
    pending_verification: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
    verified: { label: 'Verified', color: 'bg-green-500', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
};

export default function FinanceVerificationsPage() {
    const [verifications, setVerifications] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVerification, setSelectedVerification] = useState(null);
    const [showVerifyDialog, setShowVerifyDialog] = useState(false);
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    
    const [verifyData, setVerifyData] = useState({
        payment_reference: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
        split_references: [], // For split payments
    });
    
    const [rejectReason, setRejectReason] = useState('');
    const [showDetailDialog, setShowDetailDialog] = useState(false);
    const [detailVerification, setDetailVerification] = useState(null);
    const [verifyErrors, setVerifyErrors] = useState({});

    // Single search box drives filtering across all 4 tabs so finance can
    // quickly locate the customer they need to approve on urgency basis.
    // Matches: name, phone, email, course, payment method, brought-by name.
    const [searchTerm, setSearchTerm] = useState('');

    const matchesSearch = useCallback((row) => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;
        const fields = [
            row.customer_name, row.phone, row.email, row.course_name,
            row.payment_method, row.brought_by_name, row.cs_agent_name,
            row.sales_executive_name, row.transaction_id, row.payment_reference,
            row.verified_by_name, row.rejection_reason,
        ];
        return fields.some(v => v && String(v).toLowerCase().includes(q));
    }, [searchTerm]);

    const visiblePending  = useMemo(() => verifications.filter(v => v.status === 'pending_verification' && matchesSearch(v)), [verifications, matchesSearch]);
    const visibleVerified = useMemo(() => verifications.filter(v => v.status === 'verified' && matchesSearch(v)), [verifications, matchesSearch]);
    const visibleRejected = useMemo(() => verifications.filter(v => v.status === 'rejected' && matchesSearch(v)), [verifications, matchesSearch]);
    const visibleTransactions = useMemo(() => transactions.filter(t => matchesSearch(t)), [transactions, matchesSearch]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [verificationsRes, transactionsRes] = await Promise.all([
                api.get('/finance/verifications?limit=200'),
                api.get('/finance/transactions')
            ]);
            // Endpoint now returns { items, total, has_more } (previously flat array)
            const vs = verificationsRes.data?.items || verificationsRes.data || [];
            setVerifications(Array.isArray(vs) ? vs : []);
            setTransactions(transactionsRes.data?.transactions || []);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleVerify = async () => {
        if (!selectedVerification) return;
        
        // Validate transaction references
        const errors = {};
        if (selectedVerification.is_split_payment && selectedVerification.payment_splits?.length > 0) {
            // For split payments, require a reference for each split
            const splitRefs = verifyData.split_references || [];
            selectedVerification.payment_splits.forEach((split, idx) => {
                if (!splitRefs[idx]?.reference) {
                    errors[`split_${idx}`] = `Transaction reference required for ${split.method?.replace('_', ' ')}`;
                }
            });
        } else {
            // For single payment, require one reference
            if (!verifyData.payment_reference?.trim()) {
                errors.payment_reference = 'Transaction reference is required to verify payment';
            }
        }
        
        if (!verifyData.payment_date) {
            errors.payment_date = 'Payment date is required';
        }
        
        if (Object.keys(errors).length > 0) {
            setVerifyErrors(errors);
            toast.error('Please fill in all required transaction details');
            return;
        }
        
        try {
            setProcessing(true);
            await api.post(`/finance/verifications/${selectedVerification.id}/verify`, {
                ...verifyData,
                is_split_payment: selectedVerification.is_split_payment,
            });
            toast.success('Payment verified! Transaction recorded and sent to settlements.');
            setShowVerifyDialog(false);
            setSelectedVerification(null);
            setVerifyData({ payment_reference: '', payment_date: new Date().toISOString().split('T')[0], notes: '', split_references: [] });
            setVerifyErrors({});
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to verify payment');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedVerification || !rejectReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }
        
        try {
            setProcessing(true);
            await api.post(`/finance/verifications/${selectedVerification.id}/reject`, {
                rejection_reason: rejectReason
            });
            toast.success('Verification rejected');
            setShowRejectDialog(false);
            setSelectedVerification(null);
            setRejectReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject');
        } finally {
            setProcessing(false);
        }
    };

    const pendingCount = verifications.filter(v => v.status === 'pending_verification').length;
    const totalPendingAmount = verifications
        .filter(v => v.status === 'pending_verification')
        .reduce((sum, v) => sum + (v.sale_amount || 0), 0);

    return (
        <div className="space-y-6" data-testid="finance-verifications-page">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold">Finance Verifications</h1>
                    <p className="text-muted-foreground">
                        Verify enrollment payments and manage transactions
                    </p>
                </div>
                <Button variant="outline" onClick={fetchData} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Pending Verification</p>
                                <p className="text-2xl font-bold text-yellow-500">{pendingCount}</p>
                            </div>
                            <Clock className="h-8 w-8 text-yellow-500/50" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Pending Amount</p>
                                <p className="text-2xl font-bold text-orange-500">
                                    AED {totalPendingAmount.toLocaleString()}
                                </p>
                            </div>
                            <DollarSign className="h-8 w-8 text-orange-500/50" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Verified Today</p>
                                <p className="text-2xl font-bold text-green-500">
                                    {verifications.filter(v => 
                                        v.status === 'verified' && 
                                        v.verified_at?.startsWith(new Date().toISOString().split('T')[0])
                                    ).length}
                                </p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-500/50" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Transactions</p>
                                <p className="text-2xl font-bold text-blue-500">{transactions.length}</p>
                            </div>
                            <Receipt className="h-8 w-8 text-blue-500/50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search bar — drives filtering across all 4 tabs */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by customer name, phone, email, course, payment method, or agent…"
                            className="pl-10 pr-10"
                            data-testid="verifications-search"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                title="Clear search"
                                data-testid="verifications-search-clear"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {searchTerm && (
                        <p className="text-xs text-muted-foreground mt-2" data-testid="verifications-search-summary">
                            Showing <strong>{visiblePending.length}</strong> pending · <strong>{visibleVerified.length}</strong> verified · <strong>{visibleRejected.length}</strong> rejected · <strong>{visibleTransactions.length}</strong> transactions matching "{searchTerm}"
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="pending">
                <TabsList>
                    <TabsTrigger value="pending" className="relative">
                        Pending Verification
                        {pendingCount > 0 && (
                            <Badge className="ml-2 bg-yellow-500">{searchTerm ? `${visiblePending.length}/${pendingCount}` : pendingCount}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="verified">Verified</TabsTrigger>
                    <TabsTrigger value="rejected">Rejected</TabsTrigger>
                    <TabsTrigger value="transactions">Transactions</TabsTrigger>
                </TabsList>

                {/* Pending Tab */}
                <TabsContent value="pending">
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Course</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Payment Method</TableHead>
                                    <TableHead>Brought By</TableHead>
                                    <TableHead>Submitted</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visiblePending.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? `No pending verifications match "${searchTerm}"` : 'No pending verifications'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visiblePending.map((v) => (
                                        <TableRow key={v.id}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{v.customer_name}</p>
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Phone className="h-3 w-3" /> {v.phone}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>{v.course_name || '-'}</TableCell>
                                            <TableCell>
                                                <span className="font-bold text-green-500">
                                                    AED {v.sale_amount?.toLocaleString()}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <CreditCard className="h-3 w-3" />
                                                    <span className="capitalize">{v.payment_method?.replace('_', ' ') || '-'}</span>
                                                    {(v.payment_proof || v.has_payment_proof) && (
                                                        <Badge variant="secondary" className="ml-1 text-xs">Proof</Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">
                                                        {v.brought_by_name || v.cs_agent_name || v.sales_executive_name || '-'}
                                                    </span>
                                                    {(v.brought_by_role_label || v.type === 'cs_upgrade_payment') && (
                                                        <Badge variant="outline" className="w-fit mt-0.5 text-[10px] px-1 py-0">
                                                            {v.brought_by_role_label || (v.type === 'cs_upgrade_payment' ? 'Customer Service' : 'Sales')}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(v.submitted_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={async () => {
                                                            try {
                                                                const r = await api.get(`/finance/verifications/${v.id}`);
                                                                setDetailVerification(r.data || v);
                                                            } catch {
                                                                setDetailVerification(v);
                                                            }
                                                            setShowDetailDialog(true);
                                                        }}
                                                        data-testid={`view-verification-${v.id}`}
                                                    >
                                                        View Details
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="default"
                                                        className="bg-green-500 hover:bg-green-600"
                                                        onClick={async () => {
                                                            try {
                                                                const r = await api.get(`/finance/verifications/${v.id}`);
                                                                setSelectedVerification(r.data || v);
                                                            } catch {
                                                                setSelectedVerification(v);
                                                            }
                                                            setShowVerifyDialog(true);
                                                        }}
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                        Verify
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => {
                                                            setSelectedVerification(v);
                                                            setShowRejectDialog(true);
                                                        }}
                                                    >
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                        Reject
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                {/* Verified Tab */}
                <TabsContent value="verified">
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Course</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Verified By</TableHead>
                                    <TableHead>Verified At</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleVerified.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? `No verified records match "${searchTerm}"` : 'No verified records'}
                                        </TableCell>
                                    </TableRow>
                                ) : (visibleVerified.map((v) => (
                                    <TableRow key={v.id}>
                                        <TableCell className="font-medium">{v.customer_name}</TableCell>
                                        <TableCell>{v.course_name || '-'}</TableCell>
                                        <TableCell className="font-bold text-green-500">
                                            AED {v.sale_amount?.toLocaleString()}
                                        </TableCell>
                                        <TableCell>{v.verified_by_name}</TableCell>
                                        <TableCell>
                                            {v.verified_at ? new Date(v.verified_at).toLocaleString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                )))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                {/* Rejected Tab */}
                <TabsContent value="rejected">
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Rejection Reason</TableHead>
                                    <TableHead>Rejected By</TableHead>
                                    <TableHead>Rejected At</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleRejected.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? `No rejected records match "${searchTerm}"` : 'No rejected records'}
                                        </TableCell>
                                    </TableRow>
                                ) : (visibleRejected.map((v) => (
                                    <TableRow key={v.id}>
                                        <TableCell className="font-medium">{v.customer_name}</TableCell>
                                        <TableCell className="text-red-500">
                                            AED {v.sale_amount?.toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="destructive">{v.rejection_reason}</Badge>
                                        </TableCell>
                                        <TableCell>{v.verified_by_name}</TableCell>
                                        <TableCell>
                                            {v.verified_at ? new Date(v.verified_at).toLocaleString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                )))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                {/* Transactions Tab */}
                <TabsContent value="transactions">
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Transaction ID</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Course</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Payment Method</TableHead>
                                    <TableHead>Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleTransactions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? `No transactions match "${searchTerm}"` : 'No transactions'}
                                        </TableCell>
                                    </TableRow>
                                ) : (visibleTransactions.map((t) => (
                                    <TableRow key={t.id}>
                                        <TableCell className="font-mono text-xs">{t.id.slice(0, 8)}...</TableCell>
                                        <TableCell className="font-medium">{t.customer_name}</TableCell>
                                        <TableCell>{t.course_name || '-'}</TableCell>
                                        <TableCell className="font-bold text-green-500">
                                            AED {t.amount?.toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{t.payment_method || '-'}</Badge>
                                        </TableCell>
                                        <TableCell>{t.payment_date}</TableCell>
                                    </TableRow>
                                )))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Verify Dialog */}
            <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            Verify Payment
                        </DialogTitle>
                        <DialogDescription>
                            Confirm that payment has been received for this enrollment.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedVerification && (
                        <div className="space-y-4">
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Customer:</span>
                                    <span className="font-medium">{selectedVerification.customer_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Course:</span>
                                    <span>{selectedVerification.course_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Amount:</span>
                                    <span className="font-bold text-green-500">
                                        AED {selectedVerification.sale_amount?.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Payment Method:</span>
                                    <span className="capitalize">{selectedVerification.payment_method?.replace('_', ' ') || 'N/A'}</span>
                                </div>
                                {selectedVerification.transaction_id && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Transaction ID:</span>
                                        <span className="font-mono">{selectedVerification.transaction_id}</span>
                                    </div>
                                )}
                                {selectedVerification.payment_date && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Payment Date:</span>
                                        <span>{new Date(selectedVerification.payment_date).toLocaleDateString()}</span>
                                    </div>
                                )}
                                {selectedVerification.payment_notes && (
                                    <div className="pt-2 border-t">
                                        <span className="text-muted-foreground text-sm">Sales Notes:</span>
                                        <p className="text-sm mt-1">{selectedVerification.payment_notes}</p>
                                    </div>
                                )}
                            </div>
                            
                            {/* Payment Proof Image */}
                            {selectedVerification.payment_proof && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4" />
                                        Payment Proof Screenshot
                                    </Label>
                                    <div className="border rounded-lg p-2 bg-muted/50">
                                        <img 
                                            src={selectedVerification.payment_proof} 
                                            alt="Payment proof" 
                                            className="max-h-64 mx-auto rounded cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={() => window.open(selectedVerification.payment_proof, '_blank')}
                                        />
                                        {selectedVerification.payment_proof_filename && (
                                            <p className="text-xs text-muted-foreground text-center mt-2">
                                                {selectedVerification.payment_proof_filename}
                                            </p>
                                        )}
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            className="w-full mt-2"
                                            onClick={() => window.open(selectedVerification.payment_proof, '_blank')}
                                        >
                                            <ExternalLink className="h-4 w-4 mr-2" />
                                            Open Full Size
                                        </Button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Transaction Reference Entry - Required */}
                            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-4">
                                <div className="flex items-center gap-2">
                                    <Receipt className="h-5 w-5 text-amber-600" />
                                    <span className="font-medium text-amber-600">Transaction Verification Required</span>
                                </div>
                                
                                {selectedVerification.is_split_payment && selectedVerification.payment_splits?.length > 0 ? (
                                    /* Split Payment References */
                                    <div className="space-y-3">
                                        <p className="text-sm text-muted-foreground">
                                            Enter transaction reference for each payment method:
                                        </p>
                                        {selectedVerification.payment_splits.map((split, idx) => (
                                            <div key={`${split.method}-${split.amount}-${idx}`} className="p-3 bg-background rounded border space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium capitalize">{split.method?.replace('_', ' ')}</span>
                                                    <span className="font-bold text-emerald-600">AED {split.amount?.toLocaleString()}</span>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-sm">Transaction Reference *</Label>
                                                    <Input
                                                        value={verifyData.split_references?.[idx]?.reference || ''}
                                                        onChange={(e) => {
                                                            const newRefs = [...(verifyData.split_references || [])];
                                                            newRefs[idx] = { ...newRefs[idx], reference: e.target.value, method: split.method };
                                                            setVerifyData({ ...verifyData, split_references: newRefs });
                                                            if (verifyErrors[`split_${idx}`]) {
                                                                setVerifyErrors({ ...verifyErrors, [`split_${idx}`]: null });
                                                            }
                                                        }}
                                                        placeholder={`Enter ${split.method?.replace('_', ' ')} reference`}
                                                        className={verifyErrors[`split_${idx}`] ? 'border-red-500' : ''}
                                                    />
                                                    {verifyErrors[`split_${idx}`] && (
                                                        <p className="text-xs text-red-500">{verifyErrors[`split_${idx}`]}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Single Payment Reference */
                                    <div className="space-y-2">
                                        <Label>Payment Reference / Transaction ID *</Label>
                                        <Input
                                            value={verifyData.payment_reference}
                                            onChange={(e) => {
                                                setVerifyData({ ...verifyData, payment_reference: e.target.value });
                                                if (verifyErrors.payment_reference) {
                                                    setVerifyErrors({ ...verifyErrors, payment_reference: null });
                                                }
                                            }}
                                            placeholder="Enter bank/gateway transaction reference"
                                            className={verifyErrors.payment_reference ? 'border-red-500' : ''}
                                        />
                                        {verifyErrors.payment_reference && (
                                            <p className="text-xs text-red-500">{verifyErrors.payment_reference}</p>
                                        )}
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label>Payment Date *</Label>
                                    <Input
                                        type="date"
                                        value={verifyData.payment_date}
                                        onChange={(e) => {
                                            setVerifyData({ ...verifyData, payment_date: e.target.value });
                                            if (verifyErrors.payment_date) {
                                                setVerifyErrors({ ...verifyErrors, payment_date: null });
                                            }
                                        }}
                                        className={verifyErrors.payment_date ? 'border-red-500' : ''}
                                    />
                                    {verifyErrors.payment_date && (
                                        <p className="text-xs text-red-500">{verifyErrors.payment_date}</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Notes (Optional)</Label>
                                <Textarea
                                    value={verifyData.notes}
                                    onChange={(e) => setVerifyData({ ...verifyData, notes: e.target.value })}
                                    placeholder="Any additional notes..."
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleVerify} disabled={processing} className="bg-green-500 hover:bg-green-600">
                            {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                            Confirm Verification
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-500">
                            <AlertTriangle className="h-5 w-5" />
                            Reject Verification
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This will reject the payment verification and notify the sales executive.
                            The lead will be moved back to "In Progress" stage.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                        <Label>Rejection Reason *</Label>
                        <Textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Please explain why this payment is being rejected..."
                            className="mt-2"
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleReject}
                            disabled={processing || !rejectReason.trim()}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Reject
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Detail View Dialog */}
            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Receipt className="h-5 w-5 text-primary" />
                            Payment Verification Details
                        </DialogTitle>
                        <DialogDescription>
                            Review payment details before approving or rejecting
                        </DialogDescription>
                    </DialogHeader>
                    
                    {detailVerification && (
                        <div className="space-y-4">
                            {/* Customer & Course Info */}
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Customer:</span>
                                    <span className="font-medium">{detailVerification.customer_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Phone:</span>
                                    <span className="flex items-center gap-1">
                                        <Phone className="h-3 w-3" />
                                        {detailVerification.phone}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Email:</span>
                                    <span>{detailVerification.email || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Course:</span>
                                    <span>{detailVerification.course_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Brought By:</span>
                                    <span className="text-right">
                                        {detailVerification.brought_by_name
                                          || detailVerification.cs_agent_name
                                          || detailVerification.sales_executive_name
                                          || 'N/A'}
                                        {(detailVerification.brought_by_role_label
                                          || detailVerification.type === 'cs_upgrade_payment') && (
                                            <span className="ml-2 text-xs text-muted-foreground">
                                                ({detailVerification.brought_by_role_label
                                                  || (detailVerification.type === 'cs_upgrade_payment' ? 'Customer Service' : 'Sales')})
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Submitted:</span>
                                    <span>{new Date(detailVerification.submitted_at).toLocaleString()}</span>
                                </div>
                            </div>
                            
                            {/* Payment Details */}
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg space-y-3">
                                <h4 className="font-medium text-emerald-600 flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Payment Information
                                </h4>
                                
                                {/* Check for multiple payments */}
                                {detailVerification.payment_splits && detailVerification.payment_splits.length > 0 ? (
                                    <div className="space-y-3">
                                        <p className="text-sm text-muted-foreground">Split Payment ({detailVerification.payment_splits.length} methods)</p>
                                        {detailVerification.payment_splits.map((split, idx) => (
                                            <div key={`${split.method}-${split.amount}-${idx}`} className="p-3 bg-background rounded border space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium capitalize">{split.method?.replace('_', ' ')}</span>
                                                    <span className="font-bold text-emerald-600">AED {split.amount?.toLocaleString()}</span>
                                                </div>
                                                {split.phone_number && (
                                                    <div className="text-xs text-muted-foreground">
                                                        Phone: {split.phone_number}
                                                        {split.is_same_number === false && (
                                                            <Badge variant="secondary" className="ml-2">Different Number</Badge>
                                                        )}
                                                    </div>
                                                )}
                                                {split.transaction_id && (
                                                    <div className="text-xs text-muted-foreground font-mono">
                                                        Txn: {split.transaction_id}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        <div className="flex justify-between pt-2 border-t">
                                            <span className="font-medium">Total Amount:</span>
                                            <span className="font-bold text-lg text-emerald-600">
                                                AED {detailVerification.sale_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Payment Method:</span>
                                            <span className="capitalize font-medium">
                                                {detailVerification.payment_method?.replace('_', ' ') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-bold text-lg text-emerald-600">
                                                AED {detailVerification.sale_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                        {detailVerification.transaction_id && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Transaction ID:</span>
                                                <span className="font-mono">{detailVerification.transaction_id}</span>
                                            </div>
                                        )}
                                        {detailVerification.payment_date && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Payment Date:</span>
                                                <span>{new Date(detailVerification.payment_date).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {/* BNPL Phone Verification */}
                                        {['tabby', 'tamara'].includes(detailVerification.payment_method?.toLowerCase()) && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-muted-foreground">BNPL Phone:</span>
                                                <span className="flex items-center gap-2">
                                                    {detailVerification.bnpl_phone || detailVerification.phone}
                                                    {detailVerification.bnpl_same_number === false && (
                                                        <Badge variant="secondary">Different Number</Badge>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {detailVerification.payment_notes && (
                                    <div className="pt-2 border-t">
                                        <span className="text-sm text-muted-foreground">Notes:</span>
                                        <p className="text-sm mt-1">{detailVerification.payment_notes}</p>
                                    </div>
                                )}
                            </div>
                            
                            {/* Payment Proof */}
                            {detailVerification.payment_proof && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4" />
                                        Payment Proof Screenshot
                                    </Label>
                                    <div className="border rounded-lg p-2 bg-muted/50">
                                        <img 
                                            src={detailVerification.payment_proof} 
                                            alt="Payment proof" 
                                            className="max-h-64 mx-auto rounded cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={() => window.open(detailVerification.payment_proof, '_blank')}
                                        />
                                        {detailVerification.payment_proof_filename && (
                                            <p className="text-xs text-muted-foreground text-center mt-2">
                                                {detailVerification.payment_proof_filename}
                                            </p>
                                        )}
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            className="w-full mt-2"
                                            onClick={() => window.open(detailVerification.payment_proof, '_blank')}
                                        >
                                            <ExternalLink className="h-4 w-4 mr-2" />
                                            Open Full Size
                                        </Button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Multiple Payment Proofs */}
                            {detailVerification.payment_splits?.some(s => s.proof) && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4" />
                                        Payment Proofs ({detailVerification.payment_splits.filter(s => s.proof).length})
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {detailVerification.payment_splits.filter(s => s.proof).map((split, idx) => (
                                            <div key={`proof-${split.method}-${idx}`} className="border rounded-lg p-2 bg-muted/50">
                                                <p className="text-xs text-center mb-1 capitalize">{split.method?.replace('_', ' ')}</p>
                                                <img 
                                                    src={split.proof} 
                                                    alt={`Payment proof ${idx + 1}`} 
                                                    className="max-h-32 mx-auto rounded cursor-pointer hover:opacity-80"
                                                    onClick={() => window.open(split.proof, '_blank')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter className="flex gap-2 border-t pt-4">
                        <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                            Close
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowDetailDialog(false);
                                setSelectedVerification(detailVerification);
                                setShowRejectDialog(true);
                            }}
                        >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                        </Button>
                        <Button
                            className="bg-green-500 hover:bg-green-600"
                            onClick={() => {
                                setShowDetailDialog(false);
                                setSelectedVerification(detailVerification);
                                setShowVerifyDialog(true);
                            }}
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve Payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
